import _styled from "styled-components";
import { useMemo } from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { ArrowsRightLeftIcon, PencilSquareIcon, PlusIcon } from '@heroicons/react/24/solid';
import { CreditCardIcon } from '@heroicons/react/24/outline';
import { getAssetSrc } from '@restworld/utils-common';
import Title from '../title';
import { Link } from '../link';
import { Spinner } from '../spinner';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export function EmployerCompanyCard(_ref) {
  var companyAddress = _ref.companyAddress,
      companyName = _ref.companyName,
      paymentProvider = _ref.paymentProvider,
      vatNumber = _ref.vatNumber,
      lastFourDigits = _ref.lastFourDigits,
      onEdit = _ref.onEdit,
      onManagePaymentMethod = _ref.onManagePaymentMethod,
      isLoadingStripePortalLink = _ref.isLoadingStripePortalLink;
  return /*#__PURE__*/_jsxs(CardWrapper, {
    children: [/*#__PURE__*/_jsxs(CardHeaderAndInfoWrapper, {
      children: [/*#__PURE__*/_jsx(CardHeader, {
        companyName: companyName,
        onEdit: onEdit
      }), /*#__PURE__*/_jsx(CardInfo, {
        companyAddress: companyAddress,
        vatNumber: vatNumber
      })]
    }), /*#__PURE__*/_jsx(PaymentMethod, {
      paymentProvider: paymentProvider,
      lastFourDigits: lastFourDigits,
      onManagePaymentMethod: onManagePaymentMethod,
      isLoadingStripePortalLink: isLoadingStripePortalLink
    })]
  });
}

function CardWrapper(_ref2) {
  var children = _ref2.children;
  return /*#__PURE__*/_jsx(MainWrapper, {
    children: children
  });
}

function CardHeader(_ref3) {
  var companyName = _ref3.companyName,
      onEdit = _ref3.onEdit;

  var _useTranslation = useTranslation('common'),
      t = _useTranslation.t;

  return /*#__PURE__*/_jsxs(HeaderWrapper, {
    children: [/*#__PURE__*/_jsx(ComapnyNameWrapper, {
      children: /*#__PURE__*/_jsx(Title, {
        content: companyName,
        fontWeight: "semibold",
        fontSize: "base",
        twStyle: {
          "maxWidth": "90%",
          "overflow": "hidden",
          "textOverflow": "ellipsis",
          "whiteSpace": "nowrap"
        }
      })
    }), /*#__PURE__*/_jsx(EditButtonWrapper, {
      children: /*#__PURE__*/_jsx(Link, {
        StartIcon: PencilSquareIcon,
        newTab: false,
        onClick: onEdit,
        children: t('edit')
      })
    })]
  });
}

function CardInfo(_ref4) {
  var companyAddress = _ref4.companyAddress,
      vatNumber = _ref4.vatNumber;
  return /*#__PURE__*/_jsxs(CardInfoWrapper, {
    children: [/*#__PURE__*/_jsx(Title, {
      content: companyAddress,
      icon: "MapPinIconNeutral200",
      fontWeight: "medium",
      textColor: "neutral-200",
      twStyle: {
        "maxWidth": "100%",
        "overflow": "hidden",
        "textOverflow": "ellipsis",
        "whiteSpace": "nowrap",
        "fontSize": "0.875rem",
        "lineHeight": "1.25rem"
      }
    }), /*#__PURE__*/_jsx(Title, {
      content: vatNumber,
      icon: "BriefcaseIconNeutral200",
      fontWeight: "medium",
      textColor: "neutral-200",
      twStyle: {
        "fontSize": "0.875rem",
        "lineHeight": "1.25rem"
      }
    })]
  });
}

function PaymentMethod(_ref5) {
  var paymentProvider = _ref5.paymentProvider,
      lastFourDigits = _ref5.lastFourDigits,
      onManagePaymentMethod = _ref5.onManagePaymentMethod,
      isLoadingStripePortalLink = _ref5.isLoadingStripePortalLink;

  var _useTranslation2 = useTranslation('common'),
      t = _useTranslation2.t;

  var lastFourDigitsText = lastFourDigits ? "***".concat(lastFourDigits) : '';
  var paymentProviderEl = useMemo(function () {
    return paymentProvider === 'directCredit' ? /*#__PURE__*/_jsx(_Fragment, {
      children: t('direct_credit')
    }) : /*#__PURE__*/_jsx(PaymentProviderWrapper, {
      children: paymentProvider
    });
  }, [paymentProvider, t]);
  var paymentProviderLogoEl = useMemo(function () {
    return paymentProvider !== 'directCredit' && paymentProviderLogoMap[paymentProvider] ? /*#__PURE__*/_jsx(_StyledImage, {
      src: paymentProviderLogoMap[paymentProvider],
      alt: paymentProvider,
      width: 23,
      height: 16
    }) : /*#__PURE__*/_jsx(CreditCardIcon, {
      className: "text-neutral-700 h-4 w-4"
    });
  }, [paymentProvider]);
  var portalLinkLoadingEl = useMemo(function () {
    return isLoadingStripePortalLink ? /*#__PURE__*/_jsx(Spinner, {
      size: "xs",
      color: "primary"
    }) : null;
  }, [isLoadingStripePortalLink]);
  return /*#__PURE__*/_jsx(PaymentMethodWrapper, {
    children: paymentProvider ? /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsxs(PaymentInfoWrapper, {
        children: [/*#__PURE__*/_jsx(Title, {
          twStyle: {
            "marginBottom": "1rem",
            "fontSize": "0.875rem",
            "lineHeight": "1.25rem"
          },
          fontWeight: "medium",
          textColor: "neutral-700",
          children: t('payment_method')
        }), paymentProvider === 'directCredit' ? /*#__PURE__*/_jsx(ArrowsRightLeftIcon, {
          className: "text-neutral-700 h-4 w-4"
        }) : paymentProviderLogoEl, /*#__PURE__*/_jsxs(Title, {
          textColor: "neutral-700",
          fontWeight: "medium",
          twStyle: {
            "marginTop": "0.25rem",
            "fontSize": "0.875rem",
            "lineHeight": "1.25rem"
          },
          children: [paymentProviderEl, " ".concat(lastFourDigitsText)]
        })]
      }), /*#__PURE__*/_jsxs(Link, {
        size: "sm",
        variant: "restworld",
        newTab: false,
        onClick: onManagePaymentMethod,
        children: [t('manage'), portalLinkLoadingEl]
      })]
    }) : /*#__PURE__*/_jsx(AddPaymentMethodButtonWrapper, {
      children: /*#__PURE__*/_jsxs(Link, {
        variant: "restworld",
        size: "sm",
        StartIcon: PlusIcon,
        newTab: false,
        onClick: onManagePaymentMethod,
        children: [t('add_a_payment_method'), portalLinkLoadingEl]
      })
    })
  });
}

export default EmployerCompanyCard;
var paymentProviderLogoMap = {
  mastercard: getAssetSrc('/utils-assets/mastercard.svg'),
  visa: getAssetSrc('/utils-assets/visa.svg'),
  maestro: getAssetSrc('/utils-assets/maestro.svg'),
  amex: getAssetSrc('/utils-assets/amex.svg'),
  payPal: getAssetSrc('/utils-assets/paypal.svg')
};

var MainWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__MainWrapper",
  componentId: "sc-10n1qyd-0"
})(["", ""], {
  "display": "flex",
  "width": "100%",
  "flexDirection": "column",
  "justifyContent": "space-between",
  "borderRadius": "1rem",
  "borderWidth": "1px",
  "--tw-border-opacity": "1",
  "borderColor": "rgb(222 222 225 / var(--tw-border-opacity))",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(250 250 250 / var(--tw-bg-opacity))",
  "padding": "1.25rem"
});

var HeaderWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__HeaderWrapper",
  componentId: "sc-10n1qyd-1"
})(["", ""], {
  "marginBottom": "1rem",
  "display": "flex",
  "maxWidth": "100%",
  "alignItems": "center",
  "justifyContent": "space-between"
});

var CardInfoWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__CardInfoWrapper",
  componentId: "sc-10n1qyd-2"
})(["", ""], {
  "marginBottom": "1.5rem",
  "display": "flex",
  "flexDirection": "column",
  "gap": "0.5rem"
});

var PaymentMethodWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__PaymentMethodWrapper",
  componentId: "sc-10n1qyd-3"
})(["", ""], {
  "display": "flex",
  "height": "100%",
  "width": "100%",
  "alignItems": "center",
  "justifyContent": "space-between",
  "borderRadius": "0.5rem",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(240 235 248 / var(--tw-bg-opacity))",
  "padding": "0.75rem"
});

var PaymentInfoWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__PaymentInfoWrapper",
  componentId: "sc-10n1qyd-4"
})(["", ""], {});

var PaymentProviderWrapper = /*#__PURE__*/_styled.span.withConfig({
  displayName: "employer-company-card__PaymentProviderWrapper",
  componentId: "sc-10n1qyd-5"
})(["", ""], {
  "textTransform": "capitalize"
});

var ComapnyNameWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__ComapnyNameWrapper",
  componentId: "sc-10n1qyd-6"
})(["", ""], {
  "width": "75%"
});

var EditButtonWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__EditButtonWrapper",
  componentId: "sc-10n1qyd-7"
})(["", ""], {
  "display": "flex",
  "width": "25%",
  "justifyContent": "flex-end"
});

var CardHeaderAndInfoWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__CardHeaderAndInfoWrapper",
  componentId: "sc-10n1qyd-8"
})(["", ""], {});

var AddPaymentMethodButtonWrapper = /*#__PURE__*/_styled.div.withConfig({
  displayName: "employer-company-card__AddPaymentMethodButtonWrapper",
  componentId: "sc-10n1qyd-9"
})(["", ""], {
  "display": "grid",
  "height": "100%",
  "width": "100%",
  "placeItems": "center"
});

var _StyledImage = /*#__PURE__*/_styled(Image).withConfig({
  displayName: "employer-company-card___StyledImage",
  componentId: "sc-10n1qyd-10"
})({
  "objectFit": "cover",
  "objectPosition": "center"
});